import React, {useState, useEffect} from "react";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHome, faSpinner, faDownload} from '@fortawesome/free-solid-svg-icons';
import {Breadcrumb, Table, Alert, Card, Nav, Pagination, Form, Button} from '@themesberg/react-bootstrap';
import {connect} from "react-redux";
import {getFilteredTransactions} from "../../actions/transactions";
import {getToken} from "../../helpers/tokenHelper";
import moment from "moment";

const Transactions = ({getFilteredTransactions}) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    
    // Search filters - default to last 7 days (including today)
    const getDefaultStartDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 6);
        return date.toISOString().split('T')[0];
    };
    
    const getDefaultEndDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const [searchMsisdn, setSearchMsisdn] = useState('');
    const [searchTransactionId, setSearchTransactionId] = useState('');
    const [searchStartDate, setSearchStartDate] = useState(getDefaultStartDate());
    const [searchEndDate, setSearchEndDate] = useState(getDefaultEndDate());

    useEffect(() => {
        fetchTransactions();
    }, [page, pageSize]);

    const fetchTransactions = async () => {
        setLoading(true);
        setError(null);
        try {
            const filters = {
                msisdn: searchMsisdn,
                transactionId: searchTransactionId,
                startDate: searchStartDate,
                endDate: searchEndDate
            };
            const response = await getFilteredTransactions(page, pageSize, filters);
            setTransactions(response.content || []);
            setTotalPages(response.totalPages || 0);
            setTotalElements(response.totalElements || 0);
        } catch (err) {
            setError(err.message || "Failed to fetch transactions");
        } finally {
            setLoading(false);
        }
    };

    const toCamelCase = (str) => {
        if (!str) return '-';
        return str.toLowerCase().split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-RW', {
            style: 'currency',
            currency: 'RWF'
        }).format(amount);
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'SUCCESS': 'success',
            'PENDING': 'warning',
            'FAILED': 'danger'
        };
        return statusColors[status] || 'secondary';
    };

    const goToPage = (pageNum) => {
        setPage(pageNum);
    };

    const goPrev = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };

    const goNext = () => {
        if (page + 1 < totalPages) {
            setPage(page + 1);
        }
    };

    const handleSizeSelect = (e) => {
        const size = parseInt(e.target.value, 10);
        if (!isNaN(size)) {
            setPageSize(size);
            setPage(0);
        }
    };

    const handleSearch = () => {
        setPage(0);
        fetchTransactions();
    };

    const handleClearFilters = () => {
        setSearchMsisdn('');
        setSearchTransactionId('');
        setSearchStartDate('');
        setSearchEndDate('');
        setPage(0);
    };

    const handleExport = async () => {
        setExporting(true);
        setError(null);
        try {
            const token = getToken();
            const params = new URLSearchParams();
            
            if (searchMsisdn) params.append('msisdn', searchMsisdn);
            if (searchTransactionId) params.append('transactionId', searchTransactionId);
            if (searchStartDate) params.append('startDate', searchStartDate);
            if (searchEndDate) params.append('endDate', searchEndDate);

            const url = `${process.env.REACT_APP_BASE_URL}/intelligrapi/transactions/export?${params.toString()}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'appKey': 'F1'
                }
            });

            if (!response.ok) {
                throw new Error('Export failed');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `transactions_${new Date().getTime()}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            setError(err.message || "Failed to export transactions");
        } finally {
            setExporting(false);
        }
    };

    const renderPaginationItems = () => {
        const items = [];
        const maxPagesToShow = 7;

        if (totalPages <= maxPagesToShow) {
            for (let i = 0; i < totalPages; i++) {
                items.push(
                    <Pagination.Item
                        key={`page-btn-${i+1}`}
                        active={i === page}
                        onClick={() => goToPage(i)}
                    >
                        {i + 1}
                    </Pagination.Item>
                );
            }
        } else {
            items.push(
                <Pagination.Item
                    key="page-btn-1"
                    active={page === 0}
                    onClick={() => goToPage(0)}
                >
                    1
                </Pagination.Item>
            );

            let startPage, endPage;

            if (page <= 3) {
                startPage = 1;
                endPage = 4;
            } else if (page >= totalPages - 4) {
                startPage = totalPages - 5;
                endPage = totalPages - 1;
            } else {
                startPage = page - 1;
                endPage = page + 1;
            }

            if (startPage > 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }

            for (let i = startPage; i <= endPage; i++) {
                items.push(
                    <Pagination.Item
                        key={`page-btn-${i+1}`}
                        active={i === page}
                        onClick={() => goToPage(i)}
                    >
                        {i + 1}
                    </Pagination.Item>
                );
            }

            if (endPage < totalPages - 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }

            items.push(
                <Pagination.Item
                    key={`page-btn-${totalPages}`}
                    active={page === totalPages - 1}
                    onClick={() => goToPage(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const isFirst = page === 0;
    const isLast = page >= totalPages - 1;
    const numberOfElements = transactions.length;
    const hasFilters = searchMsisdn || searchTransactionId || searchStartDate || searchEndDate;

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                <div className="d-block mb-4 mb-md-0">
                    <Breadcrumb className="d-none d-md-inline-block"
                                listProps={{className: "breadcrumb-dark breadcrumb-transparent"}}>
                        <Breadcrumb.Item><FontAwesomeIcon icon={faHome}/></Breadcrumb.Item>
                        <Breadcrumb.Item active>Transactions</Breadcrumb.Item>
                    </Breadcrumb>
                    <h4>Transactions</h4>
                    <p className="mb-0">Total: {totalElements} transactions</p>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={handleExport}
                        disabled={exporting || loading}
                    >
                        <FontAwesomeIcon icon={exporting ? faSpinner : faDownload} spin={exporting} className="me-2"/>
                        {exporting ? 'Exporting...' : 'Export CSV'}
                    </Button>
                </div>
            </div>

            <Alert variant="info" className="d-flex align-items-center">
                <FontAwesomeIcon icon={faHome} className="me-2"/>
                <span>
                    Showing transactions from <strong>{moment(searchStartDate).format('MMM DD, YYYY')}</strong> to <strong>{moment(searchEndDate).format('MMM DD, YYYY')}</strong>
                    {!searchMsisdn && !searchTransactionId && ' (Last 7 days by default)'}
                </span>
            </Alert>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Search Filters */}
            <Card border="light" className="shadow-sm mb-4">
                <Card.Body>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <Form.Group>
                                <Form.Label>MSISDN (Sender/Receiver)</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by phone number"
                                    value={searchMsisdn}
                                    onChange={(e) => setSearchMsisdn(e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group>
                                <Form.Label>Transaction ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by transaction ID"
                                    value={searchTransactionId}
                                    onChange={(e) => setSearchTransactionId(e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-2">
                            <Form.Group>
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={searchStartDate}
                                    onChange={(e) => setSearchStartDate(e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-2">
                            <Form.Group>
                                <Form.Label>End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={searchEndDate}
                                    onChange={(e) => setSearchEndDate(e.target.value)}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button 
                                className="btn btn-primary me-2"
                                onClick={handleSearch}
                            >
                                Search
                            </button>
                            <button 
                                className="btn btn-secondary"
                                onClick={handleClearFilters}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card border="light" className="shadow-sm">
                <Card.Body className="pb-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary"/>
                            <p className="mt-3">Loading transactions...</p>
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">No transactions found</p>
                        </div>
                    ) : (
                        <Table responsive className="table-centered table-nowrap rounded mb-0">
                            <thead className="thead-light">
                            <tr>
                                <th className="border-0">Date</th>
                                <th className="border-0">Names</th>
                                <th className="border-0">Msisdn</th>
                                <th className="border-0">TransactionID</th>
                                <th className="border-0">Receiver</th>
                                <th className="border-0">Amount</th>
                                <th className="border-0">Status</th>
                                <th className="border-0">Type</th>
                            </tr>
                            </thead>
                            <tbody>
                            {transactions.map((transaction, index) => (
                                <tr key={transaction.id || index}>
                                    <td>{moment(transaction.createdDate).format('YYYY-MM-DD HH:mm')}</td>
                                    <td>{toCamelCase(transaction.senderName)}</td>
                                    <td>{transaction.sendingFri}</td>
                                    <td>{transaction.transactionId}</td>
                                    <td>{transaction.receivingFri}</td>
                                    <td>{formatCurrency(transaction.amount)}</td>
                                    <td>
                                        <span className={`badge bg-${getStatusBadge(transaction.status)}`}>
                                            {transaction.status}
                                        </span>
                                    </td>
                                    <td>{transaction.transactionType}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
                {!loading && transactions.length > 0 && (
                    <Card.Footer className="px-3 border-0 d-lg-flex align-items-center justify-content-between">
                        <Nav className="align-items-center">
                            <Pagination className="mb-2 mb-lg-0">
                                <Pagination.Prev onClick={goPrev} disabled={isFirst}>
                                    Previous
                                </Pagination.Prev>
                                {renderPaginationItems()}
                                <Pagination.Next onClick={goNext} disabled={isLast}>
                                    Next
                                </Pagination.Next>
                            </Pagination>
                            <div className="ms-3 d-flex align-items-center">
                                <span className="me-2">Size</span>
                                <Form.Select 
                                    size="sm"
                                    value={pageSize} 
                                    onChange={handleSizeSelect} 
                                    style={{ width: 100 }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </Form.Select>
                            </div>
                        </Nav>
                        <small className="fw-bold">
                            {(() => {
                                const start = totalElements === 0 ? 0 : (page * pageSize) + 1;
                                const end = (page * pageSize) + numberOfElements;
                                return hasFilters 
                                    ? `Showing ${numberOfElements} filtered results of ${totalElements} total`
                                    : `Showing ${start}-${end} of ${totalElements} entries`;
                            })()}
                        </small>
                    </Card.Footer>
                )}
            </Card>
        </>
    );
};

const mapDispatchToProps = {
    getFilteredTransactions
};

export default connect(null, mapDispatchToProps)(Transactions);
